import { useState, useEffect, createContext, useContext } from "react";
import { supabase } from "../lib/supabase";

// ─── Plan limits definition ───────────────────────────────────────────────────
// All money from business plans goes 100% to JiraniApp (no pro fee split).
// Pro bookings outside a plan still carry the standard 25% platform fee.

export const PLAN_LIMITS = {
  starter: {
    key:               "starter",
    label:             "Starter",
    bookingsPerMonth:  20,
    teamSlots:         3,   // reserved pros
    memberSeats:       2,   // team member logins
    locations:         1,
    reportsAccess:     false,
    priceMonthly:      4999,  // KES
    priceAnnual:       49999, // KES
  },
  growth: {
    key:               "growth",
    label:             "Growth",
    bookingsPerMonth:  100,
    teamSlots:         10,
    memberSeats:       5,
    locations:         3,
    reportsAccess:     true,
    priceMonthly:      14999,
    priceAnnual:       149999,
  },
  enterprise: {
    key:               "enterprise",
    label:             "Enterprise",
    bookingsPerMonth:  Infinity,
    teamSlots:         Infinity,
    memberSeats:       Infinity,
    locations:         Infinity,
    reportsAccess:     true,
    priceMonthly:      39999,
    priceAnnual:       399999,
  },
};

// ─── Context ──────────────────────────────────────────────────────────────────

const BusinessAccountContext = createContext(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function BusinessAccountProvider({ children }) {
  const [account, setAccount]   = useState(null);
  const [plan,    setPlan]      = useState(null);
  const [userRole, setUserRole] = useState(null); // "Owner" | "Admin" | "Booking Manager" | "Viewer"
  const [loading, setLoading]   = useState(true);
  const [error,   setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadAccount = async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();

        if (authError || !user) {
          if (!cancelled) setLoading(false);
          return;
        }

        // ── 1. Check if this user owns a business account ──────────────────
        const { data: bizAccount, error: bizError } = await supabase
          .from("business_accounts")
          .select("*")
          .eq("owner_id", user.id)
          .maybeSingle();

        if (bizError) throw bizError;

        if (bizAccount) {
          if (!cancelled) {
            setAccount(bizAccount);
            setPlan(PLAN_LIMITS[bizAccount.plan] ?? PLAN_LIMITS.starter);
            setUserRole("Owner");
          }
        } else {
          // ── 2. Check if this user is a team member on someone's business ──
          const { data: membership, error: memberError } = await supabase
            .from("business_team_members")
            .select("role, business_id, business_accounts(*)")
            .eq("member_id", user.id)
            .eq("status", "active")
            .maybeSingle();

          if (memberError) throw memberError;

          if (membership && membership.business_accounts) {
            if (!cancelled) {
              setAccount(membership.business_accounts);
              setPlan(
                PLAN_LIMITS[membership.business_accounts.plan] ?? PLAN_LIMITS.starter
              );
              setUserRole(membership.role);
            }
          }
          // If neither owner nor member — account stays null (personal user)
        }
      } catch (err) {
        console.error("[useBusinessAccount] Failed to load account:", err);
        if (!cancelled) setError(err.message ?? "Failed to load business account");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAccount();

    // ── Realtime: refresh if the business_accounts row changes ────────────
    const channel = supabase
      .channel("business_account_changes")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "business_accounts" },
        (payload) => {
          if (!cancelled && account && payload.new.id === account.id) {
            setAccount(payload.new);
            setPlan(PLAN_LIMITS[payload.new.plan] ?? PLAN_LIMITS.starter);
          }
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Helpers exposed to consumers ─────────────────────────────────────────

  /** Returns true if the current user can perform owner-only actions */
  const isOwner = userRole === "Owner";

  /** Returns true if the current user can manage bookings and team */
  const isAdminOrAbove = userRole === "Owner" || userRole === "Admin";

  /** Returns true if the current user can create bookings */
  const canBook =
    userRole === "Owner" ||
    userRole === "Admin" ||
    userRole === "Booking Manager";

  /** Returns true if the subscription is healthy enough to use the platform */
  const isActive =
    account?.subscription_status === "active" ||
    account?.subscription_status === "trialing";

  /** Returns true if payment is overdue */
  const isPastDue = account?.subscription_status === "past_due";

  return (
    <BusinessAccountContext.Provider
      value={{
        account,
        setAccount,
        plan,
        setPlan,
        userRole,
        loading,
        error,
        // helpers
        isOwner,
        isAdminOrAbove,
        canBook,
        isActive,
        isPastDue,
      }}
    >
      {children}
    </BusinessAccountContext.Provider>
  );
}

// ─── Consumer hook ────────────────────────────────────────────────────────────

export function useBusinessAccount() {

  const ctx = useContext(BusinessAccountContext);

  if (ctx === null) {

    throw new Error(

      "useBusinessAccount must be used inside a <BusinessAccountProvider>. " +

      "Wrap the relevant route or component with <BusinessAccountProvider>."

    );

  }

  return ctx;

}

// ─── Consumer hook (optional — safe outside a provider) ───────────────────────
// Use this instead of useBusinessAccount() only in components that
// legitimately render both inside and outside <BusinessAccountProvider>
// (e.g. MessagingPage.jsx, shared by personal users and business accounts).
export function useBusinessAccountOptional() {

  return useContext(BusinessAccountContext);

}